import { action, computed, observable } from 'mobx'
import { skip } from 'rxjs'
import { type IValidationSchema, validationContext } from 'validx'
import {
  type ErrorDescriptor,
  receiveErrors,
} from '@/features/legends/editor/analyzer-errors'
import { type RynoType, Types } from '@/features/legends/editor/ryno-types'
import type {
  BinaryOperationRule,
  BinaryOperationValue,
  BinaryOperator,
} from '@/features/legends/filters/rules'
import {
  validateFields,
  validateWhileEditing,
} from '@/features/legends/editor/validation'
import {
  FilterEditorViewModelBase,
  type FilterEditorViewModelOptions,
} from '@/features/legends/filters/FilterEditorViewModelBase'
import { SymbolConnectorViewModel } from '@/features/legends/symbols/SymbolConnectorViewModel'
import { tapAction, toStream } from '@/utils/mobx-util'
import { validateObject } from '@/utils/validx-util'

/**
 * Compares an answer (or any symbol) with a value.
 */
export class BinaryOperationEditorViewModel extends FilterEditorViewModelBase<BinaryOperationRule> {
  private readonly _schema: IValidationSchema<BinaryOperationEditorViewModel> =
    {
      target: [validateObject()],
      op: [({ value }) => this._validateOp(value)],
      value: [({ value }) => this._validateValue(value)],
    }

  readonly type = 'BinaryOperation'
  readonly target: SymbolConnectorViewModel

  @observable accessor op: BinaryOperator = 'Eq'
  @observable accessor value: BinaryOperationValue = null

  readonly validation = validationContext(this, this._schema)

  /**
   * Keeps the operator valid when the target's type changes.
   */
  private readonly _opFixes$ = toStream(() => this.availableOps).pipe(
    tapAction((ops) => {
      if (!ops.some(([op]) => op === this.op)) {
        this.op = ops[0][0]
      }
    }),
  )

  /**
   * Coerces the value to the target's type, and revalidates it, when the type
   * changes.
   */
  private readonly _valueCoercions$ = toStream(
    () => this.target.valueType,
    true,
  ).pipe(
    tapAction((type) => {
      if (this.value !== null) {
        this._tryCoerceValue(type)
      }
    }),
    skip(1),
    tapAction(() => {
      if (this.value !== null) {
        validateFields(this, this._schema, ['value'])
      }
    }),
  )

  constructor(options: FilterEditorViewModelOptions) {
    super(options)
    this.target = new SymbolConnectorViewModel({
      dependencies: this.dependencies,
      context: this.context,
      required: true,
    })
    this.lifecycle.manage(this.target)
    this.lifecycle.manage(validateWhileEditing(this, this._schema))
    this.lifecycle.manage(this._opFixes$)
    this.lifecycle.manage(this._valueCoercions$)
    this.lifecycle.manage(
      this.target.bubbleErrorsTo(this, this._schema, 'target'),
    )
  }

  /**
   * The type of the comparison value: the element type for lists.
   */
  @computed
  get valueInputType(): RynoType {
    const type = this.target.valueType
    return Types.isListType(type) ? type.elementType : type
  }

  /**
   * The operators that apply to the target's type, with their labels.
   */
  @computed
  get availableOps(): [BinaryOperator, string][] {
    const type = this.target.valueType
    if (Types.isListType(type)) {
      return [
        ['Contains', 'contains'],
        ['NotContains', 'does not contain'],
      ]
    }
    if (Types.isNumberType(type)) {
      return [
        ['Eq', 'is'],
        ['NotEq', 'is not'],
        ['Gt', 'greater than'],
        ['Gte', 'greater than or equal'],
        ['Lt', 'less than'],
        ['Lte', 'less than or equal'],
      ]
    }
    return [
      ['Eq', 'is'],
      ['NotEq', 'is not'],
    ]
  }

  /**
   * The target's answer options when it is a select-like question, so the
   * value can be picked from them.
   */
  @computed
  get predefinedOptions() {
    const answerOptions = this.target.symbol?.answerOptions
    return answerOptions && answerOptions.type === 'select'
      ? answerOptions.options
      : null
  }

  /**
   * Sets the operator.
   */
  @action.bound
  setOp(op: BinaryOperator) {
    this.op = op
  }

  /**
   * Sets the value to compare with.
   */
  @action.bound
  setValue(value: BinaryOperationValue) {
    this.value = value
  }

  /**
   * Sets the filter from the legend document. The value is coerced to the
   * target's type once the whole legend is loaded, so that the coercion is
   * part of what loaded rather than a change to save.
   */
  @action
  hydrate({ target, op, value }: BinaryOperationRule) {
    this.target.hydrate(target)
    if (op) {
      this.op = op
    }
    this.value = value ?? null
    this.dependencies.symbols.afterHydrate(() => {
      if (this.value !== null) {
        this._tryCoerceValue(this.target.valueType)
      }
    })
  }

  /** @inheritDoc */
  toJSON(): BinaryOperationRule {
    return {
      type: this.type,
      target: this.target.toJSON(),
      op: this.op,
      value: this.value,
    }
  }

  /** @inheritDoc */
  receiveErrors(descriptor: ErrorDescriptor) {
    const inner = descriptor.inner ?? {}
    receiveErrors(this, {
      target: inner.target,
      op: inner.op,
      value: inner.value,
    })
  }

  private _validateValue(value: unknown): true | string {
    const symbol = this.target.symbol
    if (symbol?.answerOptions && symbol.answerOptions.type === 'select') {
      return (
        symbol.answerOptions.options.some((o) => o.value === value) ||
        `${String(value)} is not an option for ${symbol.id}`
      )
    }
    const errors = this.dependencies.symbols.validateValueType(
      this.valueInputType,
      value,
    )
    return errors ? errors[0] : true
  }

  private _validateOp(op: unknown): true | string {
    if (this.value === null) {
      return true
    }
    return (
      this.availableOps.some(([available]) => available === op) ||
      `This operation is not available for type "${Types.getFriendlyTypeName(this.target.valueType)}"`
    )
  }

  @action
  private _tryCoerceValue(type: RynoType | null) {
    if (!type) {
      return
    }
    if (Types.isListType(type)) {
      this._tryCoerceValue(type.elementType)
      return
    }
    if (Types.isStringType(type)) {
      this.value =
        typeof this.value === 'boolean'
          ? this.value
            ? 'Yes'
            : 'No'
          : String(this.value)
      return
    }
    if (Types.isNumberType(type)) {
      const num = Number(this.value)
      this.value = !Number.isNaN(num) ? num : 0
      return
    }
    if (Types.isBoolType(type)) {
      this.value = Boolean(this.value)
    }
  }
}
